import { getRandomString } from '@saviynt/common';

import {
  ACTION_BUTTON_KINDS,
  CERTIFIED,
  EO_CERTIFIED,
} from '../../../components/CertsActionButtons';
import {
  CERT_ENTITLEMENT_VALUEKEY,
  CERT_EO_ROW_TYPE,
} from '../../../components/CertsDetails/CertsSidePanel';
import { getKeysForSidePanelHeader } from '../../../components/CertsDetails/CertsSidePanel/utils';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_ROLEOWNER,
  GROUP_BY_APP_VALUE,
} from '../../../utils/constant';
import { flattenList, HIERARCHY_KINDS } from '../../../utils/utils';

import {
  ACC_CERT_ENTITLEMENT_VALUEKEY,
  ENT_CERT_ENTITLEMENT_VALUEKEY,
  EO_ACC_CERT_ENTITLEMENT_VALUEKEY,
  EO_CHILD_CERT_ENTITLEMENT_VALUEKEY,
  EO_ENT_CERT_ENTITLEMENT_VALUEKEY,
  EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY,
  RLE_CERT_ENTITLEMENT_VALUEKEY,
  ROWS_NOT_TO_SHOW,
  SIDE_PANEL_CATEGORY_ACC,
  SIDE_PANEL_CATEGORY_CHILD_ENT,
  SIDE_PANEL_CATEGORY_ENT,
  SIDE_PANEL_CATEGORY_RLE,
  SIDE_PANEL_CATEGORY_USR,
  USR_CERT_ENTITLEMENT_VALUEKEY,
} from './constant';

/**
 * Determines the type of side panel to display based on the given value.
 *
 * @param {string} value - The value to determine the side panel type.
 * @returns {string} - The type of side panel to display.
 */
export const findTypeForSidePanel = (value) => {
  if (value === USR_CERT_ENTITLEMENT_VALUEKEY) {
    return SIDE_PANEL_CATEGORY_USR;
  }

  if (value === RLE_CERT_ENTITLEMENT_VALUEKEY) {
    return SIDE_PANEL_CATEGORY_RLE;
  }

  if (value === ACC_CERT_ENTITLEMENT_VALUEKEY) {
    return SIDE_PANEL_CATEGORY_ACC;
  }

  return SIDE_PANEL_CATEGORY_ENT;
};

/** EO campaign
 * Determines the type of side panel to display based on the given value.
 *
 * @param {string} value - The value to determine the side panel type.
 * @returns {string} - The type of side panel to display.
 */

// check for the RO and self certification
export const findEOTypeForSidePanel = (value) => {
  const parsedValue = value?.toString();

  if (parsedValue === EO_ENT_CERT_ENTITLEMENT_VALUEKEY) {
    return SIDE_PANEL_CATEGORY_ENT;
  }

  if (parsedValue === EO_ACC_CERT_ENTITLEMENT_VALUEKEY) {
    return SIDE_PANEL_CATEGORY_ACC;
  }

  if (parsedValue === EO_CHILD_CERT_ENTITLEMENT_VALUEKEY) {
    return SIDE_PANEL_CATEGORY_CHILD_ENT;
  }

  return SIDE_PANEL_CATEGORY_ENT;
};

/**
 * Recursively adds a tracker object to each node in a tree structure.
 *
 * @param {Array} nodes - The array of nodes to which the tracker object will be added.
 * @param {Object} [trackerObjBaseSettings={}] - Optional base settings to be included in each tracker object.
 * @param {number} [level=0] - The current level in the tree, used to set the level property in the tracker object.
 * @param {string|null} [parentEid=null] - The parent EID, used to set the parentEid property in the tracker object.
 * @returns {Array} The array of nodes with the tracker object added to each node.
 */
export const addTrackerObject = (
  isEOCampaign,
  nodes,
  trackerObjBaseSettings = {},
  level = 0,
  parentEid = null,
  parents = []
) =>
  nodes.map((node) => {
    const type = isEOCampaign
      ? findEOTypeForSidePanel(node[CERT_EO_ROW_TYPE])
      : findTypeForSidePanel(node[CERT_ENTITLEMENT_VALUEKEY]);
    const parentLabelKey =
      getKeysForSidePanelHeader(isEOCampaign, type)?.title || `${type}_LABEL`;
    const tracker = node.tracker
      ? {
          ...node.tracker,
          childCount: node?.children?.length,
        }
      : {
          id: getRandomString(),
          parentEid,
          parents: [...parents],
          isChecked: node?.checked === '1',
          isConnected: false,
          isIndeterminate: false,
          level,
          childCount: node?.children?.length,
          isAtleastOneChildChecked: false,
          ...trackerObjBaseSettings,
        };

    return {
      ...node,
      type,
      tracker,
      children:
        node.children?.length > 0
          ? addTrackerObject(
              isEOCampaign,
              node.children,
              trackerObjBaseSettings,
              level + 1,
              node?.id,
              node?.[ROWS_NOT_TO_SHOW] && node?.[ROWS_NOT_TO_SHOW] === '0'
                ? [
                    ...parents,
                    {
                      type: isEOCampaign
                        ? findEOTypeForSidePanel(node[CERT_EO_ROW_TYPE])
                        : findTypeForSidePanel(node[CERT_ENTITLEMENT_VALUEKEY]),
                      [parentLabelKey]: node[parentLabelKey],
                      isEndPoint: node?.isEndPoint,
                      level: tracker?.level,
                      trackerId: tracker?.id,
                    },
                  ]
                : [...parents]
            )
          : [],
    };
  });

/**
 * Recursively adds action tracker objects to each node in a tree structure.
 *
 * @param {Array} nodes - The array of nodes to which the action tracker objects will be added.
 * @returns {Array} The array of nodes with the action tracker objects added to each node.
 */
export const addActionsTracker = (nodes) =>
  nodes.map((node) => {
    const isUserAndStatusApproved = node?.CERTIFIED === CERTIFIED.WORKS_FOR_ME;
    const isAccountEntRoleAndStatusApproved =
      node?.CERTIFIED === CERTIFIED.APPROVE;
    const isTerminateUserSelected = node?.CERTIFIED === CERTIFIED.TERMINATED;
    const isExtendEndDateUserSelected =
      node?.CERTIFIED === CERTIFIED.EXTEND_END_DATE ||
      node?.userEndDateExtended;
    const isConditionalCertifyUserSelected =
      node?.CERTIFIED === CERTIFIED.CONDITIONAL_CERTIFIY;
    const isRejectUserSelected =
      node?.CERTIFIED === CERTIFIED.REVOKED || node?.terminatedUser;
    const isReassignSelected = node?.CERTIFIED === CERTIFIED.REVOKED;

    const isDoesNotBelongToMeSelected =
      node?.CERTIFIED === CERTIFIED.DOES_NOT_BELONG_TO_ME;

    const isConsultSelected =
      node?.isConsulted && node?.CONSULTED_USERCOUNT > 0;
    const isConsultActionAvailable =
      node?.availableAction &&
      node?.availableAction[ACTION_BUTTON_KINDS.CONSULT];

    const isDoesNotBelongToMeActionAvailable =
      node?.availableAction &&
      node?.availableAction[ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME];
    // check available actions as well for consult- Show Consult For Own Access & Disable Consult For Consultee

    const actionButtonPayload = {};

    if (isUserAndStatusApproved) {
      actionButtonPayload[ACTION_BUTTON_KINDS.WORKS_FOR_ME] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isAccountEntRoleAndStatusApproved) {
      actionButtonPayload[ACTION_BUTTON_KINDS.APPROVE] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isTerminateUserSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.TERMINATE] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isExtendEndDateUserSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.EXTEND_END_DATE] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isRejectUserSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.REJECT] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isConditionalCertifyUserSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isReassignSelected) {
      actionButtonPayload[
        ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME || ACTION_BUTTON_KINDS.REASSIGN
      ] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isDoesNotBelongToMeActionAvailable && isDoesNotBelongToMeSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    // Start : Show Consult For Own Access & Disable Consult For Consultee
    if (isConsultSelected && isConsultActionAvailable) {
      actionButtonPayload[ACTION_BUTTON_KINDS.CONSULT] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    } else if (isConsultActionAvailable) {
      actionButtonPayload[ACTION_BUTTON_KINDS.CONSULT] = {
        isSelected: false,
        isLoading: false,
        isDisabled: false,
        shouldBeRemoved: false,
      };
    }
    // End: Show Consult For Own Access & Disable Consult For Consultee

    const defaultActionMap = node?.enableAction?.reduce((actionMap, action) => {
      // eslint-disable-next-line no-param-reassign
      actionMap[action] = {
        isSelected: false,
        isLoading: false,
        isDisabled: false,
      };

      return actionMap;
    }, {});

    return {
      ...node,
      tracker: {
        ...node.tracker,
        actions: {
          ...defaultActionMap,
          ...actionButtonPayload,
          ...node?.tracker?.actions,
        },
      },
      children:
        node?.children && node?.children?.length > 0
          ? addActionsTracker(node.children)
          : [],
    };
  });

/**
 * Recursively adds action tracker objects to each node in a tree structure.
 *
 * @param {Array} nodes - The array of nodes to which the action tracker objects will be added.
 * @returns {Array} The array of nodes with the action tracker objects added to each node.
 */
export const addEOActionsTracker = (nodes) =>
  nodes.map((node) => {
    const isEntitlementAndStatusApproved =
      node?.CERTIFIED === EO_CERTIFIED.WORKS_FOR_ME;
    const isAccountChildEntAndStatusApproved =
      node?.CERTIFIED === EO_CERTIFIED.APPROVE;
    const isConditionalCertifyUserSelected =
      node?.CERTIFIED === EO_CERTIFIED.CONDITIONAL_CERTIFIY;
    const isRejectUserSelected = node?.CERTIFIED === EO_CERTIFIED.REVOKED;
    const isReassignSelected =
      node?.CERTIFIED === EO_CERTIFIED.DOES_NOT_BELONG_TO_ME;

    const isDecommissionSelected =
      node?.CERTIFIED === EO_CERTIFIED.DECOMMISSION;
    const isAccountChildEntAndStatusRejected =
      node?.CERTIFIED === EO_CERTIFIED.REVOKED;
    const isConsultSelected =
      node?.isConsulted && node?.CONSULTED_USERCOUNT > 0;
    const isConsultActionAvailable =
      node?.availableAction &&
      node?.availableAction[ACTION_BUTTON_KINDS.CONSULT];
    // check available actions as well for consult- Show Consult For Own Access & Disable Consult For Consultee

    const actionButtonPayload = {};

    if (isEntitlementAndStatusApproved) {
      actionButtonPayload[ACTION_BUTTON_KINDS.WORKS_FOR_ME] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isAccountChildEntAndStatusApproved) {
      actionButtonPayload[ACTION_BUTTON_KINDS.APPROVE] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isRejectUserSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.REJECT] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isAccountChildEntAndStatusRejected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.REJECT] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isDecommissionSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.DECOMMISSION] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isConditionalCertifyUserSelected) {
      actionButtonPayload[ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    if (isReassignSelected) {
      actionButtonPayload[
        ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME || ACTION_BUTTON_KINDS.REASSIGN
      ] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    }

    // // Start : Show Consult For Own Access & Disable Consult For Consultee
    if (isConsultSelected && isConsultActionAvailable) {
      actionButtonPayload[ACTION_BUTTON_KINDS.CONSULT] = {
        isSelected: true,
        isLoading: false,
        isDisabled: false,
      };
    } else if (isConsultActionAvailable) {
      actionButtonPayload[ACTION_BUTTON_KINDS.CONSULT] = {
        isSelected: false,
        isLoading: false,
        isDisabled: false,
        shouldBeRemoved: false,
      };
    }
    // End: Show Consult For Own Access & Disable Consult For Consultee

    const defaultActionMap = node?.enableAction?.reduce((actionMap, action) => {
      // eslint-disable-next-line no-param-reassign
      actionMap[action] = {
        isSelected: false,
        isLoading: false,
        isDisabled: false,
      };

      return actionMap;
    }, {});

    return {
      ...node,
      tracker: {
        ...node.tracker,
        actions: {
          ...defaultActionMap,
          ...actionButtonPayload,
          ...node?.tracker?.actions,
        },
      },
      children:
        node?.children && node?.children?.length > 0
          ? addEOActionsTracker(node.children)
          : [],
    };
  });

/**
 * Modifies a tree structure based on a given key.
 * This function is used to filter or search a tree by a specific key.
 *
 * @param {Array} tree - The original tree structure to be modified.
 * @param {string} [key=ROWS_NOT_TO_SHOW] - The key to filter or search in the tree.
 * @returns {Array} - The modified tree structure.
 */
export const modifyTreeForSearchOrFilter = (tree, key = ROWS_NOT_TO_SHOW) => {
  const tempTree = [];

  tree.forEach((branch) => {
    // If the branch matches the key and its value is '0',
    // add it to the temporary tree and recursively modify its children.
    if (branch?.[key] && branch?.[key] === '0') {
      tempTree.push({
        ...branch,
        children:
          (branch?.children && branch.children.length) > 0
            ? modifyTreeForSearchOrFilter(branch.children, key)
            : [],
      });
    }
    // If the branch doesn't match the key or doesn't have children,
    // add its children to the temporary tree.
    else if (branch?.children && branch.children.length > 0) {
      tempTree.push(...modifyTreeForSearchOrFilter(branch.children, key));
    }
  });

  return tempTree;
};

/**
 * This function takes a column picker list as input and returns a string of column names.
 * The column names are extracted from the keys of the column picker list and concatenated into a single string.
 *
 * @param {Object} columnPickerList - An object representing the column picker list.
 * @returns {string} - A string of column names, separated by commas.
 */
export const getColumnNamesToShow = (columnPickerList) => {
  let concatenatedString = '';

  // Iterate over each key in the column picker list
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const key in columnPickerList) {
    const values = Object.keys(columnPickerList[key]);

    concatenatedString += `${values?.join(',')},`;
  }

  concatenatedString = concatenatedString?.slice(0, -1);

  return concatenatedString;
};

/**
 * Determines the type of hierarchy based on the given tracker level.
 *
 * @param {number} trackerLevel - The level of the tracker.
 * @returns {string} - The type of hierarchy.
 */
export const getTypeOfHierarchy = (trackerLevel) => {
  switch (trackerLevel) {
    case 0:
      return HIERARCHY_KINDS.allChildren;
    case 1:
      return HIERARCHY_KINDS.both;
    case 2:
      return HIERARCHY_KINDS.parent;
    default:
      return HIERARCHY_KINDS.bothFull;
  }
};

const getNodeKey = (node) => {
  if (node.CERT_ENTITLEMENT_VALUEKEY === '-4') {
    return node.CERT_USERKEY;
  }

  if (node.CERT_ENTITLEMENT_VALUEKEY === '-2') {
    return node.CERT_ROLEKEY;
  }

  if (node.CERT_ENTITLEMENT_VALUEKEY === '0') {
    return node.CERT_ACCOUNTKEY;
  }

  if (Number(node.CERT_ENTITLEMENT_VALUEKEY) > 0) {
    return node.CERT_ENTITLEMENT_VALUEKEY;
  }

  return null;
};

const getEONodeKey = (node) => {
  if (node.ROW_TYPE?.toString() === EO_ENT_CERT_ENTITLEMENT_VALUEKEY) {
    return node.CERT_ENTITLEMENT_VALUEKEY;
  }

  if (node.ROW_TYPE?.toString() === EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY) {
    return node.ENDPOINTKEY;
  }

  return null;
};

const getRONodeKey = (node) => {
  if (node.ROW_TYPE?.toString() === EO_ENT_CERT_ENTITLEMENT_VALUEKEY) {
    return node.CERT_ROLEKEY;
  }

  if (node.ROW_TYPE?.toString() === EO_GROUP_BY_CERT_ENTITLEMENT_VALUEKEY) {
    return node.ENDPOINTKEY;
  }

  return null;
};

/**
 * Merges two arrays of records based on unique keys.
 *
 * @param {Array} input1 - The first array of records.
 * @param {Array} input2 - The second array of records.
 * @param {number} campaignType - defines the campaign type
 * @param {Array} appliedRowFilters - applied row filters
 * @returns {Array} - The merged array of records.
 */
export const mergeRecordsUtil = (
  input1,
  input2,
  campaignType,
  appliedRowFilters,
  groupByOptions
) => {
  if (!Array.isArray(input1) || !Array.isArray(input2)) return [];
  if (input1.length === 0) return input2;
  if (input2.length === 0) return input1;

  // check group by selection
  const selectedGroupByValue = groupByOptions?.find(
    (option) => option?.isChecked
  )?.value;

  const isGroupByApplication = selectedGroupByValue === GROUP_BY_APP_VALUE;

  // Helper function for campaign-specific merging
  const mergeRecordsByCampaignKey = (keyFunction) => {
    const result = [...input1];
    const nodeMap = new Map();

    result.forEach((node, index) => {
      const key = keyFunction(node);

      if (key !== null) {
        nodeMap.set(key, index);
      }
    });

    input2.forEach((newNode) => {
      const newKey = keyFunction(newNode);
      const existingIndex = newKey !== null ? nodeMap.get(newKey) : null;
      const existingNode = newKey !== null ? result[existingIndex] : null;

      if (!existingNode) {
        result.push(newNode);
      } else {
        const mergedChildren = mergeRecordsUtil(
          existingNode?.children || [],
          newNode?.children || [],
          campaignType,
          appliedRowFilters,
          groupByOptions
        );

        if (!existingNode.id && newNode.id && existingIndex != null) {
          result[existingIndex] = {
            ...existingNode,
            ...newNode,
            children: mergedChildren,
          };
        } else {
          result[existingIndex].children = mergedChildren;
        }
      }
    });

    return result;
  };

  if (CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER) {
    return mergeRecordsByCampaignKey(getEONodeKey);
  }

  if (CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ROLEOWNER) {
    return mergeRecordsByCampaignKey(getRONodeKey);
  }

  // UM, SA and AO campaigns
  const result = [...input1];
  const nodeMap = new Map();

  result.forEach((node, index) => {
    const key = getNodeKey(node);

    if (key !== null) {
      nodeMap.set(key, index);
    }
  });

  input2.forEach((newNode) => {
    let newKey = getNodeKey(newNode);

    // newkey is null when entitlement and role row  is selected

    if (
      !isGroupByApplication &&
      appliedRowFilters.length === 1 &&
      (appliedRowFilters.includes(ENT_CERT_ENTITLEMENT_VALUEKEY) ||
        appliedRowFilters.includes(RLE_CERT_ENTITLEMENT_VALUEKEY))
    ) {
      newKey = null;
    }

    const existingIndex = newKey !== null ? nodeMap.get(newKey) : null;
    const existingNode = newKey !== null ? result[existingIndex] : null;

    if (!existingNode) {
      result.push(newNode);
    } else {
      const mergedChildren = mergeRecordsUtil(
        existingNode?.children || [],
        newNode?.children || [],
        campaignType,
        appliedRowFilters,
        groupByOptions
      );

      if (!existingNode.id && newNode.id && existingIndex != null) {
        result[existingIndex] = {
          ...existingNode,
          ...newNode,
          children: mergedChildren,
        };
      } else {
        result[existingIndex].children = mergedChildren;
      }
    }
  });

  return result;
};

// export const findTracker = (nodes, criteria) => {
//   let tracker = null;

//   // Use some to iterate and stop when a valid tracker is found
//   nodes.some((node) => {
//     // Recursively search in children
//     if (node.children) {
//       const result = findTracker(node.children, criteria);

//       if (result) {
//         tracker = result;

//         return true;
//       }
//     }

//     // Check if the node matches the criteria
//     if (
//       criteria.type === 'user' &&
//       node.CERT_USERKEY === criteria.cert_userkey &&
//       node.CERT_ENTITLEMENT_VALUEKEY === CERT_ENTITLEMENT_VALUEKEY.USER
//     ) {
//       tracker = node.tracker;

//       return true;
//     }

//     if (
//       criteria.type === 'account' &&
//       node.CERT_ACCOUNTKEY === criteria.cert_accountkey &&
//       node.CERT_ENTITLEMENT_VALUEKEY === CERT_ENTITLEMENT_VALUEKEY.ACCOUNT
//     ) {
//       tracker = node.tracker;

//       return true;
//     }

//     return false; // continue iteration
//   });

//   return tracker;
// };

/**
 * Checks if any node in the given array has an eid available.
 *
 * @param {Array} nodeArray - The array of nodes to check.
 * @returns {boolean} - True if any node in the array has an id, false otherwise.
 */
export const checkIfValidNode = (nodeArray) => {
  const flattenedNodeArray = flattenList(nodeArray);
  const hasAnyNodeWithEid = flattenedNodeArray.filter((node) => node?.id);

  if (hasAnyNodeWithEid?.length > 0) {
    return true;
  }

  return false;
};

/**
 * Updates an array of nodes (merged list) with new data, modifying their actions and potentially removing nodes.
 *
 * @param {Array} originalParentNodeArray - The original array of parent nodes to be updated.
 * @param {Array} updatedNodes - The array of nodes containing updated data.
 * @param {string} actionKind - The kind of action to be applied to the nodes.
 * @param {boolean} currentState - The current state of the action button.
 * @param {string} trackerId - The ID of the tracker to be updated.
 * @param {Array} eidsToUpdate - The array of EIDs to be updated.
 * @param {number} [currentLevel=0] - The current level of recursion (default is 0).
 * @param {number|null} [highestLevelToRemove=null] - The highest level at which nodes should be removed (default is null).
 * @returns {Array} - The updated array of parent nodes.
 */
export const updateExistingNodes = (
  isEOCampaign,
  originalParentNodeArray,
  updatedNodes,
  actionKind,
  currentState,
  trackerId,
  eidsToUpdate,
  currentLevel = 0,
  highestLevelToRemove = null
) =>
  originalParentNodeArray.reduce((total, item) => {
    // Check weather id is sent to api for refreshed data
    const shouldUpdateNode = eidsToUpdate.includes(item?.id);

    if (!shouldUpdateNode) {
      const updatedItem = {
        ...item,
        children:
          item?.children?.length > 0
            ? updateExistingNodes(
                isEOCampaign,
                item?.children,
                updatedNodes,
                actionKind,
                currentState,
                trackerId,
                eidsToUpdate,
                currentLevel + 1,
                highestLevelToRemove
              )
            : [],
      };

      return [...total, updatedItem];
    }

    // Check weather to remove node or keep it
    const refreshedDataForItem = updatedNodes.find(
      (_item) => _item?.id === item?.id
    );

    const handleRemoval = (node) => {
      const cardElement = document.getElementById(node.id);

      if (cardElement) cardElement.classList.add('Cards-tree--isBeingRemoved');
    };

    if (!refreshedDataForItem) {
      if (
        highestLevelToRemove === null ||
        currentLevel < highestLevelToRemove
      ) {
        handleRemoval(item);
        // eslint-disable-next-line no-param-reassign
        highestLevelToRemove = currentLevel;
      }

      const getUpdatedChildrenItems = updateExistingNodes(
        isEOCampaign,
        item?.children || [],
        updatedNodes,
        actionKind,
        currentState,
        trackerId,
        eidsToUpdate,
        currentLevel + 1,
        highestLevelToRemove
      );

      return [...total, ...getUpdatedChildrenItems];
    }

    // Update the node and the actions
    const actions = item?.tracker?.actions || {};
    // const currentAction = actions?.[actionKind] || {};

    const newActionPayload = {};

    Object?.keys(actions)?.forEach((actionKey) => {
      newActionPayload[actionKey] = {
        isSelected: false,
        isLoading: false,
        isDisabled: false,
      };
    });

    const defaultActionMap = {
      isSelected: true,
      isLoading: false,
      isDisabled: false,
    };

    if (isEOCampaign) {
      // Setting up the buttons state based on the CERTIFIED key
      // Action(s): Works For Me, Approve
      if (refreshedDataForItem?.CERTIFIED === EO_CERTIFIED?.WORKS_FOR_ME) {
        if (item?.ROW_TYPE?.toString() === '0') {
          newActionPayload[ACTION_BUTTON_KINDS.WORKS_FOR_ME] = {
            ...defaultActionMap,
          };
        } else {
          newActionPayload[ACTION_BUTTON_KINDS.APPROVE] = {
            ...defaultActionMap,
          };
        }
      }

      // Action(s): Conditionally Certify
      if (
        refreshedDataForItem?.CERTIFIED === EO_CERTIFIED.CONDITIONAL_CERTIFIY
      ) {
        newActionPayload[ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY] = {
          ...defaultActionMap,
        };
      }

      // Action(s): Does Not Work For Me, Revoke/Reject
      if (
        refreshedDataForItem?.CERTIFIED === EO_CERTIFIED.DOES_NOT_BELONG_TO_ME
      ) {
        if (item?.ROW_TYPE?.toString() === '0') {
          newActionPayload[ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME] = {
            ...defaultActionMap,
          };
        }
      }

      // Action(s): Decommission
      if (refreshedDataForItem?.CERTIFIED === EO_CERTIFIED.DECOMMISSION) {
        if (item?.ROW_TYPE?.toString() === '0') {
          newActionPayload[ACTION_BUTTON_KINDS.DECOMMISSION] = {
            ...defaultActionMap,
          };
        } else {
          newActionPayload[ACTION_BUTTON_KINDS.REJECT] = {
            ...defaultActionMap,
          };
        }
      }

      // Action(s): Does Not Work For Me, Reassign (Reassign willl not be highlisghted in same certification)
      // if (refreshedDataForItem?.CERTIFIED === EO_CERTIFIED.REVOKED) {
      //   newActionPayload[ACTION_BUTTON_KINDS.REASSIGN] = { ...defaultActionMap };
      // }

      // Action(s): Consult
      if (
        refreshedDataForItem?.isConsulted &&
        refreshedDataForItem?.availableAction[ACTION_BUTTON_KINDS.CONSULT]
      ) {
        newActionPayload[ACTION_BUTTON_KINDS.CONSULT] = { ...defaultActionMap };
      } else if (
        refreshedDataForItem?.availableAction[ACTION_BUTTON_KINDS.CONSULT]
      ) {
        newActionPayload[ACTION_BUTTON_KINDS.CONSULT] = {
          ...defaultActionMap,
          isSelected: false,
        };
      }
    } else {
      // Setting up the buttons state based on the CERTIFIED key
      // Action(s): Works For Me, Approve
      if (refreshedDataForItem?.CERTIFIED === CERTIFIED.WORKS_FOR_ME) {
        if (item?.CERT_ENTITLEMENT_VALUEKEY === '-4') {
          newActionPayload[ACTION_BUTTON_KINDS.WORKS_FOR_ME] = {
            ...defaultActionMap,
          };
        } else {
          newActionPayload[ACTION_BUTTON_KINDS.APPROVE] = {
            ...defaultActionMap,
          };
        }
      }

      if (refreshedDataForItem?.CERTIFIED === CERTIFIED.DOES_NOT_BELONG_TO_ME) {
        if (item?.CERT_ENTITLEMENT_VALUEKEY === '0') {
          newActionPayload[ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME] = {
            ...defaultActionMap,
          };
        }
      }

      // Action(s): Terminate
      if (refreshedDataForItem?.CERTIFIED === CERTIFIED.TERMINATED) {
        newActionPayload[ACTION_BUTTON_KINDS.TERMINATE] = {
          ...defaultActionMap,
        };
      }

      // Action(s): Extend End Date
      if (
        refreshedDataForItem?.CERTIFIED === CERTIFIED.EXTEND_END_DATE ||
        refreshedDataForItem?.userEndDateExtended
      ) {
        newActionPayload[ACTION_BUTTON_KINDS.EXTEND_END_DATE] = {
          ...defaultActionMap,
        };
      }

      // Action(s): Conditionally Certify
      if (refreshedDataForItem?.CERTIFIED === CERTIFIED.CONDITIONAL_CERTIFIY) {
        newActionPayload[ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY] = {
          ...defaultActionMap,
        };
      }

      // Action(s): Does Not Work For Me, Revoke/Reject
      if (
        refreshedDataForItem?.CERTIFIED === CERTIFIED.REVOKED ||
        refreshedDataForItem?.terminatedUser
      ) {
        if (item?.CERT_ENTITLEMENT_VALUEKEY === '-4') {
          newActionPayload[ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME] = {
            ...defaultActionMap,
          };
        } else {
          newActionPayload[ACTION_BUTTON_KINDS.REJECT] = {
            ...defaultActionMap,
          };
        }
      }

      // Action(s): Does Not Work For Me, Reassign (Reassign willl not be highlisghted in same certification)
      // if (refreshedDataForItem?.CERTIFIED === CERTIFIED.REVOKED) {
      //   newActionPayload[ACTION_BUTTON_KINDS.REASSIGN] = { ...defaultActionMap };
      // }

      // Action(s): Consult
      if (
        refreshedDataForItem?.isConsulted &&
        refreshedDataForItem?.availableAction[ACTION_BUTTON_KINDS.CONSULT]
      ) {
        newActionPayload[ACTION_BUTTON_KINDS.CONSULT] = { ...defaultActionMap };
      } else if (
        refreshedDataForItem?.availableAction[ACTION_BUTTON_KINDS.CONSULT]
      ) {
        newActionPayload[ACTION_BUTTON_KINDS.CONSULT] = {
          ...defaultActionMap,
          isSelected: false,
        };
      }
    }

    const refreshedItem = {
      ...item,
      ...refreshedDataForItem,
      tracker: {
        ...item?.tracker,
        actions: newActionPayload,
      },
      children:
        item?.children?.length > 0
          ? updateExistingNodes(
              isEOCampaign,
              item?.children,
              updatedNodes,
              actionKind,
              currentState,
              trackerId,
              eidsToUpdate,
              currentLevel + 1,
              highestLevelToRemove
            )
          : [],
    };

    return [...total, refreshedItem];
  }, []);

/**
 * Updates a single node in a tree structure with a new action state based on the tracker ID.
 *
 * @param {Array} nodes - The array of nodes to be updated.
 * @param {string} actionKind - The kind of action to be updated.
 * @param {string} trackerId - The ID of the tracker to be updated.
 * @param {boolean} currentState - The current state of the action.
 * @returns {Array} - The updated array of nodes with the specified action state modified.
 */
export const updateExistingSingleNode = (
  nodes,
  actionKind,
  trackerId,
  currentState
) =>
  (Array.isArray(nodes) ? nodes : []).map((item) => {
    if (!item) return item;

    const trackerIdForItem = item.tracker?.id;
    const actions = item.tracker?.actions || {};
    const currentAction = actions[actionKind] || {};

    return {
      ...item,
      tracker: item.tracker
        ? {
            ...item.tracker,
            actions: {
              ...actions,
              [actionKind]: {
                ...currentAction,
                isSelected:
                  trackerIdForItem === trackerId
                    ? !currentState
                    : currentAction.isSelected,
                isLoading:
                  trackerIdForItem === trackerId
                    ? false
                    : currentAction.isLoading,
              },
            },
          }
        : item.tracker,
      children:
        item.children?.length > 0
          ? updateExistingSingleNode(
              item.children,
              actionKind,
              trackerId,
              currentState
            )
          : [],
    };
  });

/**
 * Recursively updates a node in a tree structure based on a tracker ID and applies a callback function to it.
 *
 * @param {Array} nodes - The array of nodes to be updated.
 * @param {string} trackerId - The ID of the tracker to be updated.
 * @param {Function} callback - The callback function to be applied to the node with the matching tracker ID.
 * @returns {Array} - The updated array of nodes with the specified node modified by the callback function.
 */
export const updateNodeWithTrackerId = (nodes, trackerId, callback) =>
  nodes.map((item) => {
    if (item.tracker?.id === trackerId) {
      callback(item);

      return { ...item };
    }

    return {
      ...item,
      children:
        item.children?.length > 0
          ? updateNodeWithTrackerId(item.children, trackerId, callback)
          : [],
    };
  });
